import { useCallback, useEffect, useRef, useState, type PointerEvent as RPE, type WheelEvent as RWE } from 'react';
import type { LoadedDocument } from '../lib/document';
import type { DocumentStrokes, Point, Stroke } from '../lib/strokes';
import { strokeToSvgPath } from '../lib/strokes';
import { clearStrokes, loadStrokes, saveStrokes } from '../lib/storage';
import { useDrawing, type DrawingState } from '../hooks/useDrawing';
import { Toolbar } from './Toolbar';
import { PageNav } from './PageNav';
import { renderPdfPage, type RenderHandle } from '../lib/pdfRender';
import { exportAnnotatedPdf, exportPagePng, exportRasterizedAsPdf } from '../lib/exportPdf';
import { useT } from '../i18n';
import { confirmDialog } from '../lib/swal';
import { sendDrawing, sendHomework, showSubmitForm, type HomeworkContext } from '../lib/submit';
import { useFullscreen } from '../hooks/useFullscreen';
import { enterFullscreen, isFullscreen as isFsNow } from '../lib/fullscreen';

type Props = {
  doc: LoadedDocument;
  onClose: () => void;
  /** Принудительно показать кнопку Submit (URL-override ?send=true) — если webhookUrl задан */
  forceSend?: boolean;
  /** Homework-режим: URL содержит studid → форма не показывается, submit прямо */
  homework?: HomeworkContext;
};

const DEFAULT_STATE: DrawingState = {
  color: '#ff2d55',
  size: 4,
  tool: 'pen',
};

const ZOOM_MIN = 1;
const ZOOM_MAX = 6;
const ZOOM_STEP = 1.4;
const MAX_CANVAS_DIM = 8000;

export function Viewer({ doc, onClose, forceSend = false, homework }: Props) {
  const t = useT();
  const pageCount =
    doc.kind === 'pdf' ? doc.pageCount :
    doc.kind === 'rasterized-pdf' ? doc.pageCount :
    1;

  const [pageIndex, setPageIndex] = useState(0);
  const [strokesByPage, setStrokesByPage] = useState<DocumentStrokes>({});
  const [undoStack, setUndoStack] = useState<DocumentStrokes[]>([]);
  const [drawState, setDrawState] = useState<DrawingState>(DEFAULT_STATE);
  const [stageSize, setStageSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [resizeTick, setResizeTick] = useState(0);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hasWebhook, setHasWebhook] = useState(false);
  const [hasWebhookUrl, setHasWebhookUrl] = useState(false);
  const [sent, setSent] = useState(false);
  const { isFs, toggle: toggleFs, available: fsAvailable } = useFullscreen();

  useEffect(() => {
    let cancelled = false;
    fetch('/admin/api.php?action=settings', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setHasWebhook(!!d?.hasWebhook);
        setHasWebhookUrl(!!d?.hasWebhookUrl);
      })
      .catch(() => { /* webhook просто не будет показан */ });
    return () => { cancelled = true; };
  }, []);

  // showSubmit:
  //   homework — если ?file=X&studid=Y, кнопка всегда видна (если webhookUrl задан)
  //   forceSend (?send=true) — переопределяет тумблер админа
  //   default — по тумблеру + URL
  const showSubmit =
    homework ? hasWebhookUrl :
    forceSend ? hasWebhookUrl :
    hasWebhook;

  // Ключ для IndexedDB:
  //   homework → studid|lessonid|fileHash (у каждого ученика своя сохранёнка)
  //   обычный → fileHash (как раньше)
  const storageKey = homework
    ? `${homework.studid}|${homework.lessonid || '0'}|${doc.fileHash}`
    : doc.fileHash;

  const stageRef = useRef<HTMLDivElement>(null);
  const pageWrapRef = useRef<HTMLDivElement>(null);
  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const activeRenderRef = useRef<RenderHandle | null>(null);

  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureRef = useRef<{
    startDist: number;
    startCenter: { x: number; y: number };
    startZoom: number;
    startPan: { x: number; y: number };
  } | null>(null);

  // homework-режим перезаписывает transient: для ученика мы хотим сохранять прогресс
  // между сессиями (по личному ключу studid|lessonid|fileHash)
  const skipPersistence = doc.transient && !homework;

  useEffect(() => {
    if (skipPersistence) return;
    let cancelled = false;
    loadStrokes(storageKey).then((saved) => {
      if (!cancelled && saved) setStrokesByPage(saved);
    });
    return () => {
      cancelled = true;
    };
  }, [storageKey, skipPersistence]);

  // Homework-режим: первый тач по странице переводит в fullscreen.
  // didAutoFsRef защищает от повторного входа если пользователь вышел из fullscreen вручную.
  const didAutoFsRef = useRef(false);
  useEffect(() => {
    if (!homework || !fsAvailable || isFsNow() || didAutoFsRef.current) return;
    const handler = () => {
      didAutoFsRef.current = true;
      enterFullscreen().catch(() => {});
    };
    document.addEventListener('pointerdown', handler, { once: true, capture: true });
    return () => document.removeEventListener('pointerdown', handler, { capture: true });
  }, [homework, fsAvailable]);

  useEffect(() => {
    if (skipPersistence) return;
    const timer = setTimeout(() => {
      saveStrokes(storageKey, strokesByPage).catch(console.error);
    }, 400);
    return () => clearTimeout(timer);
  }, [strokesByPage, storageKey, skipPersistence]);

  useEffect(() => {
    const canvas = pageCanvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;
    let cancelled = false;

    activeRenderRef.current?.cancel();
    activeRenderRef.current = null;
    setRenderError(null);

    (async () => {
      const cs = getComputedStyle(stage);
      const padX = parseFloat(cs.paddingLeft || '0') + parseFloat(cs.paddingRight || '0');
      const padY = parseFloat(cs.paddingTop || '0') + parseFloat(cs.paddingBottom || '0');
      const containerW = (stage.clientWidth - padX) || window.innerWidth;
      const containerH = (stage.clientHeight - padY) || window.innerHeight - 100;
      if (containerW < 10 || containerH < 10) return;

      try {
        if (doc.kind === 'pdf') {
          const page = await doc.pdf.getPage(pageIndex + 1);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const fitScale = Math.min(containerW / base.width, containerH / base.height);
          const dpr = window.devicePixelRatio || 1;
          let cssScale = fitScale * zoom;
          const maxScale = MAX_CANVAS_DIM / (Math.max(base.width, base.height) * dpr);
          if (cssScale > maxScale) cssScale = maxScale;
          const handle = renderPdfPage(page, canvas, cssScale);
          activeRenderRef.current = handle;
          const { cssWidth, cssHeight } = await handle.promise;
          setStageSize({ w: cssWidth, h: cssHeight });
        } else {
          // image или rasterized-pdf — оба пути рендерятся как картинка
          let bitmap: ImageBitmap;
          if (doc.kind === 'image') {
            bitmap = doc.imageBitmap;
          } else {
            // rasterized-pdf: загружаем PNG страницу по индексу
            const url = doc.pageUrls[pageIndex];
            const res = await fetch(url, { cache: 'force-cache' });
            if (!res.ok) throw new Error('page ' + res.status);
            const blob = await res.blob();
            bitmap = await createImageBitmap(blob);
          }
          if (cancelled) return;
          const fitScale = Math.min(containerW / bitmap.width, containerH / bitmap.height);
          const cssScale = fitScale * zoom;
          const cssWidth = Math.floor(bitmap.width * cssScale);
          const cssHeight = Math.floor(bitmap.height * cssScale);
          const dpr = window.devicePixelRatio || 1;
          let pxScale = dpr;
          const maxDim = Math.max(cssWidth, cssHeight) * dpr;
          if (maxDim > MAX_CANVAS_DIM) pxScale = MAX_CANVAS_DIM / Math.max(cssWidth, cssHeight);
          canvas.width = Math.floor(cssWidth * pxScale);
          canvas.height = Math.floor(cssHeight * pxScale);
          canvas.style.width = cssWidth + 'px';
          canvas.style.height = cssHeight + 'px';
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.setTransform(pxScale, 0, 0, pxScale, 0, 0);
          ctx.clearRect(0, 0, cssWidth, cssHeight);
          ctx.drawImage(bitmap, 0, 0, cssWidth, cssHeight);
          setStageSize({ w: cssWidth, h: cssHeight });
        }
      } catch (e) {
        if (cancelled) return;
        const err = e as Error;
        if (err.name === 'RenderingCancelledException') return;
        console.error('render failed', err);
        setRenderError(err.message || 'render failed');
      }
    })();

    return () => {
      cancelled = true;
      activeRenderRef.current?.cancel();
      activeRenderRef.current = null;
    };
  }, [doc, pageIndex, resizeTick, zoom]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    let lastW = el.clientWidth;
    let lastH = el.clientHeight;
    let first = true;
    const ro = new ResizeObserver(() => {
      if (first) {
        first = false;
        return;
      }
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (Math.abs(w - lastW) < 4 && Math.abs(h - lastH) < 4) return;
      lastW = w;
      lastH = h;
      setResizeTick((tk) => tk + 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pushUndo = useCallback(() => {
    setUndoStack((s) => [...s.slice(-49), strokesByPage]);
  }, [strokesByPage]);

  const onStrokeComplete = useCallback(
    (s: Stroke) => {
      pushUndo();
      setStrokesByPage((prev) => ({
        ...prev,
        [pageIndex]: [...(prev[pageIndex] ?? []), s],
      }));
    },
    [pageIndex, pushUndo]
  );

  const onErase = useCallback(
    (pt: Point) => {
      setStrokesByPage((prev) => {
        const list = prev[pageIndex] ?? [];
        if (!list.length) return prev;
        const threshold = 0.025;
        const next = list.filter(
          (stroke) =>
            !stroke.points.some((p) => {
              const dx = p.x - pt.x;
              const dy = p.y - pt.y;
              return dx * dx + dy * dy < threshold * threshold;
            })
        );
        if (next.length === list.length) return prev;
        return { ...prev, [pageIndex]: next };
      });
    },
    [pageIndex]
  );

  const { currentStroke, handlers: drawHandlers } = useDrawing({
    state: drawState,
    onStrokeComplete,
    onErase,
    containerRef: pageWrapRef,
  });

  const wrappedHandlers = {
    onPointerDown: (e: RPE) => {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 1) {
        drawHandlers.onPointerDown(e);
      } else if (pointersRef.current.size === 2) {
        // прерываем текущий штрих и стартуем pinch
        drawHandlers.onPointerCancel(e);
        startGesture();
      }
    },
    onPointerMove: (e: RPE) => {
      const p = pointersRef.current.get(e.pointerId);
      if (!p) return;
      p.x = e.clientX;
      p.y = e.clientY;
      if (gestureRef.current && pointersRef.current.size >= 2) {
        updateGesture();
      } else if (pointersRef.current.size === 1) {
        drawHandlers.onPointerMove(e);
      }
    },
    onPointerUp: (e: RPE) => {
      pointersRef.current.delete(e.pointerId);
      if (pointersRef.current.size < 2) {
        gestureRef.current = null;
      }
      drawHandlers.onPointerUp(e);
    },
    onPointerCancel: (e: RPE) => {
      pointersRef.current.delete(e.pointerId);
      gestureRef.current = null;
      drawHandlers.onPointerCancel(e);
    },
    onPointerLeave: (e: RPE) => {
      pointersRef.current.delete(e.pointerId);
      drawHandlers.onPointerLeave(e);
    },
  };

  function startGesture() {
    const pts = Array.from(pointersRef.current.values()).slice(0, 2);
    if (pts.length < 2) return;
    const [p1, p2] = pts;
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    gestureRef.current = {
      startDist: dist || 1,
      startCenter: center,
      startZoom: zoom,
      startPan: pan,
    };
  }

  function updateGesture() {
    const g = gestureRef.current;
    if (!g) return;
    const pts = Array.from(pointersRef.current.values()).slice(0, 2);
    if (pts.length < 2) return;
    const [p1, p2] = pts;
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    const newZoom = clamp(g.startZoom * (dist / g.startDist), ZOOM_MIN, ZOOM_MAX);
    const dx = center.x - g.startCenter.x;
    const dy = center.y - g.startCenter.y;
    setZoom(newZoom);
    setPan({ x: g.startPan.x + dx, y: g.startPan.y + dy });
  }

  function onWheel(e: RWE) {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setZoom((z) => clamp(z * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP), ZOOM_MIN, ZOOM_MAX));
  }

  function zoomIn() {
    setZoom((z) => clamp(z * ZOOM_STEP, ZOOM_MIN, ZOOM_MAX));
  }
  function zoomOut() {
    setZoom((z) => clamp(z / ZOOM_STEP, ZOOM_MIN, ZOOM_MAX));
  }
  function zoomReset() {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }

  // если зум сошёл к 1 — сбросим pan, иначе картинка будет смещена
  useEffect(() => {
    if (zoom <= 1.001 && (pan.x !== 0 || pan.y !== 0)) {
      setPan({ x: 0, y: 0 });
    }
  }, [zoom, pan.x, pan.y]);

  function onUndo() {
    setUndoStack((s) => {
      if (!s.length) return s;
      setStrokesByPage(s[s.length - 1]);
      return s.slice(0, -1);
    });
  }

  function onClearPage() {
    pushUndo();
    setStrokesByPage((prev) => ({ ...prev, [pageIndex]: [] }));
  }

  async function onClearAll() {
    const ok = await confirmDialog({
      title: t('confirm.clearAll'),
      confirmText: t('confirm.yes'),
      cancelText: t('confirm.no'),
      danger: true,
    });
    if (!ok) return;
    setUndoStack([]);
    setStrokesByPage({});
    await clearStrokes(doc.fileHash);
  }

  async function onExportPdf() {
    if (doc.kind === 'pdf') await exportAnnotatedPdf(doc, strokesByPage);
    else if (doc.kind === 'rasterized-pdf') await exportRasterizedAsPdf(doc, strokesByPage);
  }

  async function onSubmitDrawing() {
    if (sent) return;
    if (homework) {
      const ok = await sendHomework(doc, strokesByPage, homework, t);
      if (ok) {
        setSent(true);
        await clearStrokes(storageKey).catch(console.error);
      }
      return;
    }
    const form = await showSubmitForm(t);
    if (!form) return;
    const ok = await sendDrawing(doc, strokesByPage, form, t);
    if (ok) setSent(true);
  }

  async function onExportPng() {
    const canvas = pageCanvasRef.current;
    if (!canvas) return;
    await exportPagePng(canvas, stageSize, currentPageStrokes, doc.fileName, pageIndex);
  }

  const currentPageStrokes: Stroke[] = strokesByPage[pageIndex] ?? [];

  return (
    <div className="viewer">
      <Toolbar
        state={drawState}
        onStateChange={setDrawState}
        onUndo={onUndo}
        canUndo={undoStack.length > 0}
        onClearPage={onClearPage}
        onClearAll={onClearAll}
        onExportPdf={(doc.kind === 'pdf' || doc.kind === 'rasterized-pdf') ? onExportPdf : undefined}
        onExportPng={onExportPng}
        onClose={onClose}
        fileName={doc.fileName}
        zoom={zoom}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomReset={zoomReset}
        isFullscreen={isFs}
        onToggleFullscreen={fsAvailable ? toggleFs : undefined}
      />
      <div className="stage" ref={stageRef} onWheel={onWheel}>
        <div
          ref={pageWrapRef}
          className="page-wrap"
          style={{
            width: stageSize.w || 1,
            height: stageSize.h || 1,
            opacity: stageSize.w ? 1 : 0,
            transform: `translate(${pan.x}px, ${pan.y}px)`,
          }}
        >
          <canvas ref={pageCanvasRef} className="page-canvas" />
          {stageSize.w > 0 && (
            <svg
              className="draw-overlay"
              viewBox={`0 0 ${stageSize.w} ${stageSize.h}`}
              width={stageSize.w}
              height={stageSize.h}
              {...wrappedHandlers}
            >
              {currentPageStrokes.map((s) => (
                <path key={s.id} d={strokeToSvgPath(s, stageSize.w, stageSize.h)} fill={s.color} />
              ))}
              {currentStroke && (
                <path
                  d={strokeToSvgPath(currentStroke, stageSize.w, stageSize.h)}
                  fill={currentStroke.color}
                />
              )}
            </svg>
          )}
        </div>
        {renderError && <div className="render-error">{t('page.error')}: {renderError}</div>}
      </div>
      <PageNav
        page={pageIndex}
        pageCount={pageCount}
        onChange={setPageIndex}
        onSubmit={showSubmit ? onSubmitDrawing : undefined}
        submitDone={sent}
      />
    </div>
  );
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
